// Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import colors from '../Constants/colors';
const menuItems = [
  { icon: 'fas fa-tachometer-alt', text: 'Dashboard', to: '/dashboard' },
  { icon: 'fas fa-user', text: 'Profile', to: '/profile-management' },
  { icon: 'fas fa-chalkboard-teacher', text: 'Academics', to: '/courses-management' },
  { icon: 'fas fa-file-alt', text: 'Examination', to: '/examination-management' },
  { icon: 'fas fa-money-bill-wave', text: 'Fees', to: '/fee-management' },
  { icon: 'fas fa-bell', text: 'Notifications', to: '/notification-management' },
  { icon: 'fas fa-sign-out-alt', text: 'Logout', to: 'logout' },
];
// Desktop Sidebar
const DesktopSidebar = () => {
  const location = useLocation();
  const [activeIndex, setActiveIndex] = useState(null);
  const navigate = useNavigate();

  const handleClick = (index) => setActiveIndex(index);

  // logout logic (unchanged)
  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/login');
    window.location.reload();
  };

  const desktopItemBase =
    'flex items-center space-x-3 p-2.5 w-full rounded-md transition-all duration-300 cursor-pointer';

  return (
    <aside
      className="w-56 min-h-screen sticky top-0 left-0 text-white shadow-2xl z-50"
      style={{
        background: colors.newGradientSidebar,
        boxShadow: `4px 0 15px ${colors.cardShadow}`,
      }}>
      <div className="p-6">
        <ul className="space-y-5">
          {menuItems.map((item, index) => {
            // LOGOUT – styled exactly like other (inactive) items
            if (item.text === 'Logout') {
              return (
                <li key={index} className="rounded-md overflow-hidden">
                  <button
                    onClick={handleLogout}
                    className={`${desktopItemBase} bg-white text-gray-800 hover:bg-blue-100 hover:text-blue-700 hover:shadow-md`}
                  >
                    <i className={`${item.icon} text-lg`}></i>
                    <span className="font-semibold">{item.text}</span>
                  </button>
                </li>
              );
            }

            const isActive =
              location.pathname === item.to || activeIndex === index;

            return (
              <li key={index} className="rounded-md overflow-hidden">
                <Link
                  to={item.to}
                  onClick={() => handleClick(index)}
                  className={`${desktopItemBase} ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-800 to-blue-600 text-white shadow-md scale-[1.02]'
                      : 'bg-white text-gray-800 hover:bg-blue-100 hover:text-blue-700 hover:shadow-md'
                  }`}
                >
                  <i className={`${item.icon} text-lg`}></i>
                  <span className="font-semibold">{item.text}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
};
// Mobile Sidebar
const MobileSidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // logout logic (unchanged)
  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const mobileItemBase =
    'flex items-center space-x-4 p-3 w-full transition-all duration-200 cursor-pointer';

  return (
    <aside
      className={`fixed top-0 left-0 h-full w-64 text-white transform transition-transform duration-300 ease-in-out shadow-lg z-50 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
      style={{
        background: colors.newGradientSidebar,
        boxShadow: `4px 0 15px ${colors.cardShadow}`,
      }}
    >
      <div className="p-6">
        <ul className="space-y-5">
          {menuItems.map((item, index) => {
            // LOGOUT – same style pattern as other items in mobile
            if (item.text === 'Logout') {
              return (
                <li key={index} className="rounded-md overflow-hidden">
                  <button
                    onClick={handleLogout}
                    className={`${mobileItemBase} hover:bg-blue-700 hover:shadow-lg`}
                  >
                    <i className={`${item.icon} text-gray-100 text-lg`}></i>
                    <span className="text-lg font-medium">{item.text}</span>
                  </button>
                </li>
              );
            }

            return (
              <li key={index} className="rounded-md overflow-hidden">
                <Link
                  to={item.to}
                  onClick={() => {
                    toggleSidebar();
                  }}
                  className={`${mobileItemBase} ${
                    location.pathname === item.to
                      ? 'bg-gradient-to-r from-blue-800 to-blue-600 text-white shadow-md scale-[1.02]'
                      : 'hover:bg-blue-700 hover:shadow-lg'
                  }`}
                >
                  <i className={`${item.icon} text-gray-100 text-lg`}></i>
                  <span className="text-lg font-medium">{item.text}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
};

// Main Sidebar Component
const Sidebar = ({ isOpen, toggleSidebar }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // keeping your original effect logic, only for responsive behavior
  useEffect(() => {
    if (!isMobile && isOpen) {
      toggleSidebar();
    }
  }, [isMobile]);

  return isMobile ? (
    <MobileSidebar isOpen={isOpen} toggleSidebar={toggleSidebar} />
  ) : (
    <DesktopSidebar />
  );
};

export default Sidebar;
